import { apiRequest } from '../api/api-client';
import { type SessionUser } from './session.types';

/**
 * Llamadas de autenticacion.
 *
 * El refresh token no aparece por ninguna parte, y es deliberado: viaja en una
 * cookie `httpOnly` que el navegador adjunta solo. Este codigo no puede leerla
 * -ni deberia-, de modo que un XSS tampoco.
 */

/** Lo que un endpoint de sesion devuelve del actor, en crudo. */
interface PerfilCrudo {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly mustChangePassword: boolean;
  readonly permissions: readonly string[];
}

interface LoginResponse {
  readonly accessToken: string;
  readonly user: PerfilCrudo;
}

/** Los permisos pasan a conjunto: se consultan en cada boton protegido. */
function aSesion(perfil: PerfilCrudo): SessionUser {
  return {
    id: perfil.id,
    email: perfil.email,
    fullName: perfil.fullName,
    mustChangePassword: perfil.mustChangePassword,
    permissions: new Set(perfil.permissions),
  };
}

/**
 * `/auth/me` devuelve mas de lo que la sesion guarda -documento y ultimo
 * acceso, entre otros-. Se ignora a proposito: nada de la interfaz lo lee, y
 * guardarlo obligaria a que el inicio de sesion tambien lo trajera.
 */
type MeResponse = PerfilCrudo;

export interface Credentials {
  readonly email: string;
  readonly password: string;
}

/**
 * Entra y devuelve la sesion completa, en UNA sola peticion.
 *
 * Antes se pedia `/auth/me` justo despues, porque la respuesta del login no
 * traia `mustChangePassword` y sin el no se sabia a donde ir. Ese campo ya
 * viene aqui, asi que el segundo viaje sobra: era un viaje de ida y vuelta
 * entero en el momento en que el usuario esta mirando la pantalla, esperando.
 */
export async function login(
  credentials: Credentials,
): Promise<{ accessToken: string; user: SessionUser }> {
  const respuesta = await apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    body: credentials,
  });

  return { accessToken: respuesta.accessToken, user: aSesion(respuesta.user) };
}

/**
 * Renueva la sesion con la cookie.
 *
 * No recibe parametros: el navegador adjunta la credencial. Devuelve solo el
 * access token nuevo; el refresh se renueva en la cookie, fuera de vista.
 */
export function refresh(): Promise<{ accessToken: string }> {
  return apiRequest<{ accessToken: string }>('/auth/refresh', { method: 'POST' });
}

export function logout(allDevices = false): Promise<{ revokedSessions: number }> {
  return apiRequest<{ revokedSessions: number }>('/auth/logout', {
    method: 'POST',
    body: { allDevices },
  });
}

export async function fetchCurrentUser(): Promise<SessionUser> {
  return aSesion(await apiRequest<MeResponse>('/auth/me'));
}

export function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ closedSessions: number }> {
  return apiRequest<{ closedSessions: number }>('/auth/change-password', {
    method: 'POST',
    body: input,
  });
}
